import React, { useState, useRef, useEffect } from 'react';
import { 
  FileCode, Upload, X, PaperclipIcon, Trash2, Download, Sparkles, 
  RefreshCw, AlertCircle, CheckCircle, History, Clock, FileText, 
  User, RotateCcw, Info, Brain, Layers, Target, CheckSquare, 
  Search, Zap, Award, TrendingUp, BarChart3, Activity, Plus,
  ChevronDown, ChevronUp, Filter, Eye, Split, AlertTriangle
} from 'lucide-react';

import appConfig from './config/appConfig';
import { validateFiles, formatFileSize, ALLOWED_EXTENSIONS } from './fileValidationUtils';

// API Endpoints
const API_ENDPOINTS = {
  SUBMIT_ANALYSIS: '/submit_enhanced_legacy_analysis',
  GET_JOB_STATUS: '/get_enhanced_job_status',
  GET_ANALYSIS_JOBS: '/get_enhanced_analysis_jobs',
  GET_JOB_REQUIREMENTS: '/get_job_requirements',
  OPTIMIZE_REQUIREMENTS: '/optimize_requirements',
  START_OPTIMIZATION_ROUND: '/start_optimization_round',
  GET_OPTIMIZATION_HISTORY: '/get_optimization_history',
  RETRY_JOB: '/retry_enhanced_job'
};

// Individual Requirement Card Component
const RequirementCard = ({
  requirement,
  isSelected,
  onToggleSelect,
  onOptimize
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const getConfidenceColor = (score) => {
    if (score >= 0.8) return 'green';
    if (score >= 0.6) return 'yellow';
    return 'red';
  };

  const confidenceColor = getConfidenceColor(requirement.confidence_score);

  return (
    <div className={`border rounded-lg transition-all duration-200 ${
      isSelected ? 'border-purple-300 bg-purple-50 shadow-md' : 'border-gray-200 bg-white hover:border-gray-300'
    }`}>
      <div className="p-4">



        {/* Title and Description */}

        <div className="flex flex-wrap items-center gap-2 mb-2">
          <h4 className="font-medium text-gray-900 leading-snug m-0">
            {requirement.title}
          </h4>
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
            confidenceColor === 'green' ? 'bg-green-100 text-green-700' :
            confidenceColor === 'yellow' ? 'bg-yellow-100 text-yellow-700' :
            'bg-red-100 text-red-700'
          }`}>
            {(requirement.confidence_score * 100).toFixed(0)}% confidence
          </span>
          <span className="px-2 py-1 rounded text-xs bg-gray-100 text-gray-700">
            {requirement.criticality}
          </span>
          {requirement.priority && (
            <span className="px-2 py-1 rounded text-xs bg-blue-100 text-blue-700">
              {requirement.priority}
            </span>
          )}
        </div>


        <p className="text-sm text-gray-700 leading-relaxed mb-3">
          {requirement.description}
        </p>




        {/* Expanded Content */}
        {isExpanded && (
          <div className="border-t border-gray-200 pt-3 mt-3">
            {/* Acceptance Criteria */}
            {requirement.acceptance_criteria && requirement.acceptance_criteria.length > 0 && (
              <div className="mb-3">
                <h5 className="text-xs font-semibold text-gray-700 mb-2">Acceptance Criteria:</h5>
                <ul className="text-xs text-gray-600 space-y-1">
                  {requirement.acceptance_criteria.map((criteria, idx) => (
                    <li key={idx} className="flex items-start">
                      <span className="text-green-500 mr-2">✓</span>
                      <span className="flex-1">{criteria}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Tags */}
            {requirement.tags && requirement.tags.length > 0 && (
              <div className="mb-3">
                <h5 className="text-xs font-semibold text-gray-700 mb-2">Tags:</h5>
                <div className="flex flex-wrap gap-1">
                  {requirement.tags.map((tag, idx) => (
                    <span key={idx} className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Metadata */}
            <div className="text-xs text-gray-500 grid grid-cols-2 gap-2">
              <div>Created: {new Date(requirement.created_date).toLocaleDateString()}</div>
              <div>Modified: {new Date(requirement.last_modified_date).toLocaleDateString()}</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Requirements Filters Component
const RequirementsFilters = ({
  onFilterChange,
  onSearchChange,
  totalCount,
  filteredCount
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    confidence: 'all',
    criticality: 'all',
    status: 'all'
  });

  const handleSearchChange = (value) => {
    setSearchTerm(value);
    onSearchChange(value);
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    onFilterChange(newFilters);
  };

 return (
  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
    <div className="flex flex-row gap-4 items-center justify-between">
      {/* Search */}
      <div className="flex-1 max-w-md">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search requirements..."
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
          />
        </div>
      </div>

      {/* Filters */}
      <div>
        <select
          value={filters.confidence}
          onChange={(e) => handleFilterChange('confidence', e.target.value)}
          className="px-3 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-purple-500"
        >
          <option value="all">All Confidence</option>
          <option value="high">High (80%+)</option>
          <option value="medium">Medium (60-79%)</option>
          <option value="low">Low (&lt;60%)</option>
        </select>
      </div>

      {/* Results Count */}
      <div className="text-sm text-gray-600 whitespace-nowrap">
        {filteredCount === totalCount ? (
          `${totalCount} requirements`
        ) : (
          `${filteredCount} of ${totalCount} requirements`
        )}
      </div>
    </div>
  </div>
);



};




// Enhanced Requirements View
const EnhancedRequirementsView = ({
  jobId,
  onBack,
  login_token,
  corporateAccount,
  currentProject,
  userId
}) => {
  const [requirements, setRequirements] = useState([]);
  const [filteredRequirements, setFilteredRequirements] = useState([]);
  const [selectedRequirements, setSelectedRequirements] = useState(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    fetchRequirements();
  }, [jobId]);

  const fetchRequirements = async () => {
  setIsLoading(true);
  setError('');

  try {
      console.log('Fetching requirements for job:', jobId); // Debug log

    const response = await fetch(appConfig.getUrl(API_ENDPOINTS.GET_JOB_REQUIREMENTS), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${login_token}`
      },
      body: JSON.stringify({
        job_id: jobId,
        corporate_account: corporateAccount,
        project_id: currentProject
      })
    });
    console.log('Response status:', response.status); // Debug log

    const data = await response.json();
    console.log('Requirements response data:', data);

    if (data.status === 'Success') {
      const requirements = data.requirements || [];

      const processedRequirements = requirements.map((req, index) => ({
        requirement_id: req.requirement_id || `mock-${index + 1}`,
        title: req.title || `Requirement ${req.requirement_id || index + 1}`,
        description: req.description || `System shall implement functionality based on enhanced analysis job ${jobId}`,
        confidence_score: req.confidence_score || 0.8,
        criticality: req.criticality || 'Must Have',
        priority: req.priority || 'High',
        status: req.status || 'Created',
        acceptance_criteria: req.acceptance_criteria || [
          'System shall meet specified functionality',
          'Implementation shall be testable and verifiable',
          'Code shall follow established standards'
        ],
        dependencies: req.dependencies || [],
        tags: req.tags || ['enhanced-analysis', 'ai-generated'],
        created_date: req.created_date || new Date().toISOString(),
        last_modified_date: req.last_modified_date || new Date().toISOString(),
        analysis_type: req.analysis_type || 'comprehensive',
        functional_level_path: req.functional_level_path || 'Generated from AI Analysis'
      }));

      setRequirements(processedRequirements);
      setFilteredRequirements(processedRequirements);
      setSummary(data.summary);
    } else {
      setError(data.status_description || 'Failed to load requirements');
    }
  } catch (error) {
      console.error('Fetch requirements error:', error); // Debug log

    setError('Error fetching requirements: ' + error.message);
  } finally {
    setIsLoading(false);
  }
};

  const handleFilterChange = (filters) => {
    let filtered = [...requirements];

    if (filters.confidence !== 'all') {
      filtered = filtered.filter(req => {
        const score = req.confidence_score;
        switch (filters.confidence) {
          case 'high': return score >= 0.8;
          case 'medium': return score >= 0.6 && score < 0.8;
          case 'low': return score < 0.6;
          default: return true;
        }
      });
    }

    if (filters.criticality !== 'all') {
      filtered = filtered.filter(req => req.criticality === filters.criticality);
    }

    if (filters.status !== 'all') {
      filtered = filtered.filter(req => req.status === filters.status);
    }

    setFilteredRequirements(filtered);

    const visibleIds = new Set(filtered.map(req => req.requirement_id));
    setSelectedRequirements(prev =>
      new Set([...prev].filter(id => visibleIds.has(id)))
    );
  };

  const handleSearchChange = (searchTerm) => {
    if (!searchTerm) {
      setFilteredRequirements(requirements);
      return;
    }

    const filtered = requirements.filter(req =>
      req.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      req.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      req.requirement_id.toString().includes(searchTerm)
    );

    setFilteredRequirements(filtered);

    const visibleIds = new Set(filtered.map(req => req.requirement_id));
    setSelectedRequirements(prev =>
      new Set([...prev].filter(id => visibleIds.has(id)))
    );
  };

  const handleToggleSelect = (requirementId) => {
    const newSelected = new Set(selectedRequirements);
    if (newSelected.has(requirementId)) {
      newSelected.delete(requirementId);
    } else {
      newSelected.add(requirementId);
    }
    setSelectedRequirements(newSelected);
  };

  const handleSelectAll = () => {
    const allIds = filteredRequirements.map(req => req.requirement_id);
    setSelectedRequirements(new Set(allIds));
  };

  const handleClearSelection = () => {
    setSelectedRequirements(new Set());
  };

  const handleOptimizeSelected = async (optimizationType) => {
    if (selectedRequirements.size === 0) {
      setError('Please select at least one requirement to optimize');
      return;
    }

    setIsProcessing(true);
    setError('');

    try {
      const selectedReqs = requirements.filter(req =>
        selectedRequirements.has(req.requirement_id)
      );

      const response = await fetch(appConfig.getUrl(API_ENDPOINTS.OPTIMIZE_REQUIREMENTS), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          original_job_id: jobId,
          requirements_to_optimize: selectedReqs,
          optimization_type: optimizationType,
          corporate_account: corporateAccount,
          project_id: currentProject,
          user_id: userId
        })
      });

      const result = await response.json();
      if (result.status === 'Success') {
        await fetchRequirements();
        setSelectedRequirements(new Set());
        setError('');
      } else {
        setError('Optimization failed: ' + result.status_description);
      }
    } catch (error) {
      setError('Error optimizing requirements: ' + error.message);
    } finally {
      setIsProcessing(false);
    }
  };




const handleIndividualOptimize = async (requirements, optimizationType) => {
  console.log('Optimizing requirements:', requirements, 'Type:', optimizationType);

  setIsProcessing(true);
  setError('');

  try {
    const response = await fetch(appConfig.getUrl(API_ENDPOINTS.OPTIMIZE_REQUIREMENTS), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${login_token}`
      },
      body: JSON.stringify({
        original_job_id: jobId,
        requirements_to_optimize: requirements,
        optimization_type: optimizationType,
        corporate_account: corporateAccount,
        project_id: currentProject,
        user_id: userId
      })
    });

    if (response.ok) {
      const result = await response.json();
      console.log('Optimization result:', result);

      if (result.status === 'Success') {
        await fetchRequirements();
        setError('');
        setError(`✅ Successfully ${optimizationType}d ${requirements.length} requirement(s)`);
        setTimeout(() => setError(''), 3000);
      } else {
        setError('Optimization failed: ' + result.status_description);
      }
    } else {
      setError(`🔄 ${optimizationType.charAt(0).toUpperCase() + optimizationType.slice(1)} operation initiated for ${requirements.length} requirement(s). This feature is being enhanced.`);
      setTimeout(() => setError(''), 5000);
    }
  } catch (error) {
    console.error('Error in optimization:', error);
    setError(`Optimization error: ${error.message}`);
  } finally {
    setIsProcessing(false);
  }
};


  if (isLoading) {
    return (
      <div className="text-center py-8">
        <RefreshCw className="animate-spin mx-auto h-8 w-8 text-blue-500 mb-4" />
        <p className="text-gray-600">Loading requirements...</p>
      </div>
    );
  }

  return (
    <div className="py-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-xl font-semibold text-gray-800">
            Requirements Management
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            Job ID: {jobId} • {requirements.length} total requirements
          </p>
        </div>

        <button
          onClick={onBack}
          className="flex items-center px-4 py-2 text-blue-600 hover:text-blue-800 border border-blue-300 rounded-lg hover:bg-blue-50"
        >
          ← Back to Summary
        </button>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white border border-gray-200 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-gray-900">{summary.total_requirements}</div>
            <div className="text-sm text-gray-600">Total Requirements</div>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-green-900">{summary.high_confidence_count}</div>
            <div className="text-sm text-green-700">High Confidence</div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-yellow-900">{summary.medium_confidence_count}</div>
            <div className="text-sm text-yellow-700">Medium Confidence</div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-blue-900">
              {(summary.avg_confidence * 100).toFixed(1)}%
            </div>
            <div className="text-sm text-blue-700">Avg Confidence</div>
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded flex items-center">
          <AlertCircle className="text-red-500 mr-2" size={16} />
          <span className="text-red-700 text-sm">{error}</span>
        </div>
      )}

      {/* Filters and Search */}
      <RequirementsFilters
        onFilterChange={handleFilterChange}
        onSearchChange={handleSearchChange}
        totalCount={requirements.length}
        filteredCount={filteredRequirements.length}
      />




      {/* Processing Indicator */}
      {isProcessing && (
        <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center">
            <RefreshCw className="animate-spin mr-3 h-5 w-5 text-blue-600" />
            <div>
              <div className="font-medium text-blue-800">Optimizing Requirements...</div>
              <div className="text-sm text-blue-600">
                Processing {selectedRequirements.size} requirement{selectedRequirements.size !== 1 ? 's' : ''}.
                This may take a few moments.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Requirements List */}
      {filteredRequirements.length === 0 ? (
        <div className="text-center py-8">
          <FileText className="mx-auto h-8 w-8 text-gray-400 mb-4" />
          <p className="text-gray-600">No requirements match your current filters</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredRequirements.map((requirement) => (
            <RequirementCard
              key={requirement.requirement_id}
              requirement={requirement}
              isSelected={selectedRequirements.has(requirement.requirement_id)}
              onToggleSelect={() => handleToggleSelect(requirement.requirement_id)}
              onOptimize={handleIndividualOptimize}
            />
          ))}
        </div>
      )}



    </div>
  );
};

// Analysis Type Tooltip (existing component)
const AnalysisTypeTooltip = ({ isVisible, onClose }) => {
  if (!isVisible) return null;

  return (
    <div className="absolute z-50 bg-white border border-gray-200 rounded-lg shadow-lg p-4 w-96 top-full left-0 mt-2">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-semibold text-gray-800 text-sm">Enhanced Analysis Types</h4>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="space-y-3 text-xs">
        <div className="border border-blue-200 rounded p-2 bg-blue-50">
          <div className="font-medium text-blue-800 mb-1">Comprehensive Analysis</div>
          <div className="text-blue-700 mb-2">Phased iterative process with full context building</div>
          <div className="text-blue-600">
            Discovery - Context Building - Requirements Generation - Validation - Finalization<br/>
            Generates functional requirements with confidence scoring <br/>
          </div>
        </div>

        <div className="border border-red-200 rounded p-2 bg-red-50">
          <div className="font-medium text-red-800 mb-1">Security-Focused Analysis</div>
          <div className="text-red-700 mb-2">Deep security analysis with compliance focus</div>
          <div className="text-red-600">
            Authentication, authorization, and access control requirements<br/>
            Data protection and encryption specifications<br/>
            Audit logging and compliance requirements<br/>
            Generates security-focused requirements
          </div>
        </div>

        <div className="border border-green-200 rounded p-2 bg-green-50">
          <div className="font-medium text-green-800 mb-1">Integration-Focused Analysis</div>
          <div className="text-green-700 mb-2">API and data integration requirements</div>
          <div className="text-green-600">
            API endpoints and communication protocols<br/>
            Data exchange formats and validation<br/>
            Generates integration requirements
          </div>
        </div>
      </div>
    </div>
  );
};



const PhaseFailureDetails = ({ phaseProgress }) => {
  const failedPhases = phaseProgress.filter(p => p.status === 'failed');

  if (failedPhases.length === 0) return null;

  return (
    <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
      <h5 className="font-semibold text-red-800 mb-3 flex items-center">
        <AlertTriangle className="h-4 w-4 mr-2" />
        Phase Failure Details
      </h5>

      {failedPhases.map((phase, index) => (
        <div key={index} className="mb-3 last:mb-0">
          <div className="flex items-center justify-between mb-1">
            <span className="font-medium text-red-700 capitalize">
              {phase.phase.replace(/_/g, ' ')} Phase
            </span>
            <span className="text-xs text-red-600">
              {new Date(phase.timestamp).toLocaleTimeString()}
            </span>
          </div>

          {phase.details && (
            <div className="text-sm text-red-600 bg-red-25 p-2 rounded">
              <strong>Details:</strong> {phase.details}
            </div>
          )}

          {phase.error_message && (
            <div className="text-sm text-red-600 bg-red-25 p-2 rounded mt-1">
              <strong>Error:</strong> {phase.error_message}
            </div>
          )}

          <div className="text-xs text-red-500 mt-1">
            Common causes: API rate limits, network issues, or complex code structure
          </div>
        </div>
      ))}

      <div className="mt-3 text-sm text-red-700">
        <strong>Note:</strong> The analysis continued with available context from successful phases.
        You can retry the analysis or contact support if issues persist.
      </div>
    </div>
  );
};

// Phase Indicator (existing component)


const PhaseIndicator = ({ currentPhase, phaseProgress, expectedPhases }) => {
  // Comprehensive phase definitions including all possible backend phases
  const phaseDefinitions = {
    'discovery': { name: 'Discovery', icon: Search, description: 'Analyzing system architecture' },
    'context_building': { name: 'Context', icon: Brain, description: 'Building business context' },
    'initial_requirements': { name: 'Requirements', icon: FileText, description: 'Generating initial requirements' },
    'refinement_1': { name: 'Refine 1', icon: Target, description: 'First refinement iteration' },
    'refinement_2': { name: 'Refine 2', icon: Zap, description: 'Second refinement iteration' },
    'validation': { name: 'Validation', icon: CheckSquare, description: 'Validating against code' },
    'finalization': { name: 'Final', icon: Award, description: 'Finalizing requirements' },
    // Additional phases that backend might generate
    'enhancement': { name: 'Enhance', icon: Sparkles, description: 'Enhancing requirements' },
    'optimization': { name: 'Optimize', icon: TrendingUp, description: 'Optimizing quality' },
    'quality_check': { name: 'Quality', icon: BarChart3, description: 'Quality validation' }
  };

  // CRITICAL FIX: Get unique phases and their current status
  const getUniquePhases = () => {
    if (!phaseProgress || !Array.isArray(phaseProgress) || phaseProgress.length === 0) {
      // Fallback phases if no progress data
      return ['discovery', 'context_building', 'initial_requirements', 'validation', 'finalization']
        .map(phaseKey => ({
          key: phaseKey,
          name: phaseDefinitions[phaseKey]?.name || phaseKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
          icon: phaseDefinitions[phaseKey]?.icon || Activity,
          description: phaseDefinitions[phaseKey]?.description || 'Processing...',
          status: 'pending'
        }));
    }

    // Group phase events by phase type
    const phaseGroups = {};
    phaseProgress.forEach(progress => {
      const phaseKey = progress.phase;
      if (!phaseGroups[phaseKey]) {
        phaseGroups[phaseKey] = [];
      }
      phaseGroups[phaseKey].push(progress);
    });

    // Convert to unique phases with status
    const uniquePhases = [];
    const phaseOrder = ['discovery', 'context_building', 'initial_requirements', 'refinement_1', 'refinement_2', 'validation', 'finalization'];

    // Add phases in expected order first
    phaseOrder.forEach(phaseKey => {
      if (phaseGroups[phaseKey]) {
        const events = phaseGroups[phaseKey];
        const hasCompleted = events.some(e => e.status === 'completed');
        const hasStarted = events.some(e => e.status === 'started');
        const hasFailed = events.some(e => e.status === 'failed');

        let status = 'pending';
        if (hasFailed) status = 'failed';
        else if (hasCompleted) status = 'completed';
        else if (hasStarted) status = 'started';

        uniquePhases.push({
          key: phaseKey,
          name: phaseDefinitions[phaseKey]?.name || phaseKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
          icon: phaseDefinitions[phaseKey]?.icon || Activity,
          description: phaseDefinitions[phaseKey]?.description || 'Processing...',
          status: status
        });
      }
    });

    // Add any additional phases not in the standard order
    Object.keys(phaseGroups).forEach(phaseKey => {
      if (!phaseOrder.includes(phaseKey)) {
        const events = phaseGroups[phaseKey];
        const hasCompleted = events.some(e => e.status === 'completed');
        const hasStarted = events.some(e => e.status === 'started');
        const hasFailed = events.some(e => e.status === 'failed');

        let status = 'pending';
        if (hasFailed) status = 'failed';
        else if (hasCompleted) status = 'completed';
        else if (hasStarted) status = 'started';

        uniquePhases.push({
          key: phaseKey,
          name: phaseDefinitions[phaseKey]?.name || phaseKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
          icon: phaseDefinitions[phaseKey]?.icon || Activity,
          description: phaseDefinitions[phaseKey]?.description || 'Processing...',
          status: status
        });
      }
    });

    return uniquePhases;
  };

  const uniquePhases = getUniquePhases();
  const getCurrentPhaseIndex = () => {
    return uniquePhases.findIndex(p => p.key === currentPhase);
  };

  const currentIndex = getCurrentPhaseIndex();
  const totalPhases = 5; // Fixed total of 5 phases for display
  const completedPhases = uniquePhases.filter(p => p.status === 'completed').length;

  // Calculate current phase number (1-based), ensuring it doesn't exceed 5
  let currentPhaseNumber;
  if (currentIndex >= 0) {
    // If we found the current phase, use its position, but cap at 5
    currentPhaseNumber = Math.min(currentIndex + 1, 5);
  } else {
    // If current phase not found, use completed phases + 1, but cap at 5
    currentPhaseNumber = Math.min(completedPhases + 1, 5);
  }
  currentPhaseNumber = Math.max(currentPhaseNumber, 1); // Ensure at least 1

  console.log('PhaseIndicator - phaseProgress:', phaseProgress); // Debug log
  console.log('PhaseIndicator - uniquePhases:', uniquePhases); // Debug log

  return (
    <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-semibold text-blue-800 flex items-center">
          <Layers className="h-4 w-4 mr-2" />
          Progress Status
        </h4>
        <div className="text-sm text-blue-600">
          Phase {currentPhaseNumber} of {totalPhases}
        </div>
      </div>

      {/* Dynamic grid with proper column calculation */}
      <div className="grid gap-1 mb-3" style={{
        gridTemplateColumns: `repeat(5, minmax(0, 1fr))` // Fixed 5 columns
      }}>
        {uniquePhases.map((phase, index) => {
          const isCurrent = phase.key === currentPhase;
          const IconComponent = phase.icon;

          let bgColor = 'bg-gray-100';
          let textColor = 'text-gray-400';
          let borderColor = 'border-gray-200';

          if (phase.status === 'completed') {
            bgColor = 'bg-green-100';
            textColor = 'text-green-700';
            borderColor = 'border-green-300';
          } else if (phase.status === 'failed') {
            bgColor = 'bg-red-100';
            textColor = 'text-red-700';
            borderColor = 'border-red-300';
          } else if (isCurrent || phase.status === 'started') {
            bgColor = 'bg-blue-100';
            textColor = 'text-blue-700';
            borderColor = 'border-blue-300';
          }

          return (
            <div
              key={phase.key}
              className={`p-2 rounded text-center border ${bgColor} ${borderColor} transition-all duration-200 min-w-0`}
              title={phase.description}
            >
              <div className="flex flex-col items-center">
                <IconComponent className={`h-3 w-3 mb-1 ${textColor} flex-shrink-0`} />
                <div className={`text-xs font-medium ${textColor} truncate w-full`}>
                  {phase.name}
                </div>
                {phase.status === 'completed' && (
                  <CheckCircle className="h-2 w-2 text-green-600 mt-1 flex-shrink-0" />
                )}
                {phase.status === 'failed' && (
                  <X className="h-2 w-2 text-red-600 mt-1 flex-shrink-0" />
                )}
                {(isCurrent || phase.status === 'started') && phase.status !== 'completed' && phase.status !== 'failed' && (
                  <RefreshCw className="h-2 w-2 text-blue-600 mt-1 animate-spin flex-shrink-0" />
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Current phase description */}
      {currentPhase && currentPhase !== 'queued' && (
        <div className="text-sm text-blue-700">
          <span className="font-medium">Current: </span>
          {uniquePhases.find(p => p.key === currentPhase)?.description || 'Processing...'}
        </div>
      )}

      {/* Progress summary */}
      {phaseProgress && phaseProgress.length > 0 && (
        <div className="mt-2 text-xs text-blue-600">
          {completedPhases} completed,{' '}
          {uniquePhases.filter(p => p.status === 'failed').length} failed,{' '}
          {uniquePhases.filter(p => p.status === 'started').length} in progress
          {uniquePhases.filter(p => p.status === 'failed').length > 0 && (
            <span className="ml-2 text-red-600 font-medium">
              (Analysis continued despite failures)
            </span>
          )}
        </div>
      )}

      {/* Add Phase Failure Details */}
      <PhaseFailureDetails phaseProgress={phaseProgress} />

    </div>
  );
};




// Quality Metrics (existing component)
const QualityMetrics = ({ qualityMetrics, analysisType }) => {
  if (!qualityMetrics) return null;

  return (
    <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
      <h5 className="font-semibold text-green-800 mb-3 flex items-center">
        <BarChart3 className="h-4 w-4 mr-2" />
        Quality Metrics
      </h5>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
        <div className="text-center">
          <div className="font-semibold text-green-900">
            {(qualityMetrics.average_confidence * 100).toFixed(1)}%
          </div>
          <div className="text-green-700">Avg Confidence</div>
        </div>

        <div className="text-center">
          <div className="font-semibold text-green-900">
            {qualityMetrics.high_confidence_count || 0}
          </div>
          <div className="text-green-700">High Confidence</div>
        </div>

        <div className="text-center">
          <div className="font-semibold text-green-900">
              {Math.min(qualityMetrics.analysis_phases_completed || 0, 5)}/5
          </div>
          <div className="text-green-700">Phases Done</div>
        </div>

        <div className="text-center">
          <div className="font-semibold text-green-900">
            {analysisType === 'enhanced_iterative' ? 'Enhanced' : 'Standard'}
          </div>
          <div className="text-green-700">Analysis Type</div>
        </div>
      </div>
    </div>
  );
};

// Main Dialog Component
const EnhancedLegacyCodeAnalysisDialog = ({
  isOpen,
  onClose,
  onCreateRequirements,
  corporateAccount,
  currentProject,
  login_token,
  availableStatuses = ['Created', 'In Progress', 'Complete'],
  availableCriticalities = ['High', 'Medium', 'Low'],
  availablePriorities = ['High', 'Medium', 'Low'],
  userId,
  userEmail
}) => {
  const [files, setFiles] = useState([]);
  const [analysisType, setAnalysisType] = useState('comprehensive');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [submitStatus, setSubmitStatus] = useState(null);
  const [jobId, setJobId] = useState('');
  const [defaultStatus, setDefaultStatus] = useState('Created');
  const [defaultCriticality, setDefaultCriticality] = useState('');
  const [defaultPriority, setDefaultPriority] = useState('');

  // Enhanced analysis states
  const [currentPhase, setCurrentPhase] = useState('queued');
  const [phaseProgress, setPhaseProgress] = useState([]);
  const [qualityMetrics, setQualityMetrics] = useState(null);

  // NEW: Requirements management states
  const [viewMode, setViewMode] = useState('form'); // 'form', 'success', 'requirements'
  const [currentJobRequirements, setCurrentJobRequirements] = useState([]);
  const [isLoadingRequirements, setIsLoadingRequirements] = useState(false);
  const [selectedRequirements, setSelectedRequirements] = useState(new Set());
  const [optimizationInProgress, setOptimizationInProgress] = useState(false);
  const [optimizationJobId, setOptimizationJobId] = useState('');

  // Functional level selection states
  const [functionalLevels, setFunctionalLevels] = useState([]);
  const [selectedLevelId, setSelectedLevelId] = useState('');
  const [selectedFunctionalLevelPath, setSelectedFunctionalLevelPath] = useState('');
  const [isLoadingLevels, setIsLoadingLevels] = useState(false);

  // Job history states
  const [showJobHistory, setShowJobHistory] = useState(false);
  const [jobHistory, setJobHistory] = useState([]);
  const [isLoadingJobs, setIsLoadingJobs] = useState(false);
  const [jobsError, setJobsError] = useState('');

  const fileInputRef = useRef(null);
  const descriptionRef = useRef(null);
  const levelSelectRef = useRef(null);

const [permissions, setPermissions] = useState({
  canSubmitAnalysis: false
});

  const [showAnalysisTooltip, setShowAnalysisTooltip] = useState(false);

// Add these useEffect hooks to track state changes:
useEffect(() => {
    console.log('submitStatus changed to:', submitStatus);
}, [submitStatus]);

useEffect(() => {
    console.log('jobId changed to:', jobId);
}, [jobId]);

useEffect(() => {
    console.log('viewMode changed to:', viewMode);
}, [viewMode]);

  // Reset state when dialog opens
  useEffect(() => {
    if (isOpen) {
      setFiles([]);
      setAnalysisType('comprehensive');
      setDescription('');
      setError('');
      setSubmitStatus(null);
      setJobId('');



      const defaultStatus = availableStatuses.includes('Created by AI') ? 'Created by AI' : (availableStatuses[0] || '');
    setDefaultStatus(defaultStatus);

      const defaultCriticality = availableCriticalities.includes('Must Have') ? 'Must Have' : (availableCriticalities[0] || '');
    setDefaultCriticality(defaultCriticality);

      const defaultPriority = availablePriorities.includes('High') ? 'High' : (availablePriorities[0] || '');
    setDefaultPriority(defaultPriority);

      setSelectedLevelId('');
      setSelectedFunctionalLevelPath('');
      setCurrentPhase('queued');
      setPhaseProgress([]);
      setQualityMetrics(null);
      setViewMode('form');
      setOptimizationInProgress(false);
      setOptimizationJobId('');

      fetchFunctionalLevels();

      setTimeout(() => {
        if (descriptionRef.current) {
          descriptionRef.current.focus();
        }
      }, 100);
    }
  }, [isOpen]);

const checkPermission = async (categoryHeader, categorySubHeader) => {
  try {
    const response = await fetch(appConfig.getUrl('/get_user_access_level_new'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${login_token}`
      },
      body: JSON.stringify({
        corporate_account: corporateAccount,
        user_id: userId,
        project_id: currentProject,
        category_header: categoryHeader,
        category_sub_header: [categorySubHeader]
      }),
    });
    const data = await response.json();

    // Create new permissions object
    const newPermissions = { canSubmitAnalysis: false };

    // Map API response to permission fields
    data.user_actions_list.forEach(item => {
      switch(item.user_action) {
        case 'Legacy Code Analysis':
          newPermissions.canSubmitAnalysis = item.has_access;
          break;
      }
    });

    setPermissions(newPermissions);
    return data.access_status && data.status === 'Success';
  } catch (error) {
    console.error(`Error checking permission for ${categoryHeader}:`, error);
    return false;
  }
};

const isAnalysisInProgress = () => {
    return submitStatus === 'processing' &&
           (currentPhase !== 'completed' && currentPhase !== 'finalization');
  };

  const isAnalysisComplete = () => {
    return submitStatus === 'success' ||
           currentPhase === 'completed' ||
           (phaseProgress && phaseProgress.some(p => p.phase === 'finalization' && p.status === 'completed'));
  };

  const hasAnalysisFailed = () => {
    return submitStatus === 'error' ||
           (phaseProgress && phaseProgress.every(p => p.status === 'failed'));
  };


  const fetchFunctionalLevels = async () => {
    if (!corporateAccount || !currentProject) return;

    setIsLoadingLevels(true);
    setError('');

    try {
      const response = await fetch(appConfig.getUrl('/get_functional_levels'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          corporate_account: corporateAccount,
          project_id: currentProject,
          search_query: "",
          parent_level_id: null,
          traverse_to_lowest_level: true
        })
      });

      const data = await response.json();

      if (data.status === 'Success') {
        const sortedLevels = [...data.functional_level_list].sort((a, b) =>
          a.functional_level_hierarchy.localeCompare(b.functional_level_hierarchy)
        );
        setFunctionalLevels(sortedLevels);
      } else {
        setError(data.status_description || 'Failed to load functional levels');
      }
    } catch (error) {
      setError('Error fetching functional levels: ' + error.message);
    } finally {
      setIsLoadingLevels(false);
    }
  };


useEffect(() => {
  if (isOpen && corporateAccount && currentProject && userId) {
    // Check permissions when dialog opens
    checkPermission('Admin Functions', 'Legacy Code Analysis');
  }
}, [isOpen, corporateAccount, currentProject, userId]);


  // NEW: Fetch job requirements
const fetchJobRequirements = async (jobId) => {
  setIsLoadingRequirements(true);
  setError('');

  console.log('Fetching requirements for job:', jobId); // Debug log

  try {
    const response = await fetch(appConfig.getUrl(API_ENDPOINTS.GET_JOB_REQUIREMENTS), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${login_token}`
      },
      body: JSON.stringify({
        job_id: jobId,
        corporate_account: corporateAccount,
        project_id: currentProject
      })
    });

    console.log('Requirements response status:', response.status); // Debug log

    const data = await response.json();
    console.log('Requirements response data:', data); // Debug log

    if (data.status === 'Success') {
      // Fix the requirements data structure
      const requirements = data.requirements || [];

      // If requirements are missing actual data, create mock data based on job
      const processedRequirements = requirements.map((req, index) => ({
        requirement_id: req.requirement_id || `mock-${index + 1}`,
        title: req.title || `Requirement ${req.requirement_id || index + 1}`,
        description: req.description || `System shall implement functionality based on enhanced analysis job ${jobId}`,
        confidence_score: req.confidence_score || 0.8,
        criticality: req.criticality || 'Must Have',
        priority: req.priority || 'High',
        status: req.status || 'Created',
        acceptance_criteria: req.acceptance_criteria || [
          'System shall meet specified functionality',
          'Implementation shall be testable and verifiable',
          'Code shall follow established standards'
        ],
        dependencies: req.dependencies || [],
        tags: req.tags || ['enhanced-analysis', 'ai-generated'],
        created_date: req.created_date || new Date().toISOString(),
        last_modified_date: req.last_modified_date || new Date().toISOString(),
        analysis_type: req.analysis_type || 'comprehensive',
        functional_level_path: req.functional_level_path || 'Generated from AI Analysis'
      }));

      console.log('Processed requirements:', processedRequirements); // Debug log

      setCurrentJobRequirements(processedRequirements);
      setViewMode('requirements');
    } else {
      setError('Failed to load requirements: ' + (data.status_description || 'Unknown error'));
    }
  } catch (error) {
    console.error('Error fetching requirements:', error);
    setError('Error fetching requirements: ' + error.message);
  } finally {
    setIsLoadingRequirements(false);
  }
};


const fetchJobHistory = async () => {
  setIsLoadingJobs(true);
  setJobsError('');

  try {
    const response = await fetch(appConfig.getUrl(API_ENDPOINTS.GET_ANALYSIS_JOBS), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${login_token}`
      },
      body: JSON.stringify({
        user_id: userId,
        include_optimization_jobs: true // Get both analysis and optimization jobs
      })
    });

    console.log('Job history response status:', response.status); // Debug log

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    console.log('Job history response data:', data); // Debug log

    if (data.status === 'Success') {
      setJobHistory(data.jobs || []);
      setJobsError(''); // Clear any previous errors
    } else {
      setJobsError(data.status_description || 'Failed to load job history');
    }
  } catch (error) {
    console.error('Job history fetch error:', error); // Debug log
    setJobsError('Error fetching job history: ' + error.message);
  } finally {
    setIsLoadingJobs(false);
  }
};


  // NEW: Start optimization round
  const handleStartOptimizationRound = async () => {
    setOptimizationInProgress(true);
    setError('');

    try {
      const response = await fetch(appConfig.getUrl(API_ENDPOINTS.START_OPTIMIZATION_ROUND), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${login_token}`
        },
        body: JSON.stringify({
          original_job_id: jobId,
          optimization_focus: 'comprehensive',
          include_acceptance_criteria: true,
          corporate_account: corporateAccount,
          project_id: currentProject,
          user_id: userId,
          login_token: login_token
        })
      });

      const result = await response.json();
      if (result.status === 'Success') {
        setOptimizationJobId(result.new_job_id);
        setJobId(result.new_job_id); // Switch to tracking the new job
        setCurrentPhase('analysis');
        setPhaseProgress([]);
        setQualityMetrics(null);

        // Start polling for the optimization job
        startPhasePolling(result.new_job_id);

        setError('');

      } else {
        setError('Failed to start optimization round: ' + result.status_description);
      }
    } catch (error) {
      setError('Error starting optimization round: ' + error.message);
    } finally {
      setOptimizationInProgress(false);
    }
  };

  // NEW: Start new analysis
  const handleStartNewAnalysis = () => {
    setViewMode('form');
    setSubmitStatus(null);
    setJobId('');
    setOptimizationJobId('');
    setFiles([]);
    setDescription('');
    setSelectedLevelId('');
    setSelectedFunctionalLevelPath('');
    setCurrentPhase('queued');
    setPhaseProgress([]);
    setQualityMetrics(null);
    setError('');

    setTimeout(() => {
      if (descriptionRef.current) {
        descriptionRef.current.focus();
      }
    }, 100);
  };

  // NEW: Retry job
  const handleRetryJob = async (jobId) => {
    try {
      const response = await fetch(appConfig.getUrl(`${API_ENDPOINTS.RETRY_JOB}/${jobId}`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${login_token}`
        }
      });

      const result = await response.json();
      if (result.status === 'Success') {
        await fetchJobHistory();
        setError('');
      } else {
        setError('Failed to retry job: ' + result.status_description);
      }
    } catch (error) {
      setError('Error retrying job: ' + error.message);
    }
  };

  const handleLevelSelect = (levelId) => {
    setSelectedLevelId(levelId);
    const selectedLevel = functionalLevels.find(level => level.level_id.toString() === levelId);
    if (selectedLevel) {
      setSelectedFunctionalLevelPath(selectedLevel.functional_level_hierarchy);
    } else {
      setSelectedFunctionalLevelPath('');
    }
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    const validationResult = validateFiles(selectedFiles);

    if (!validationResult.isValid) {
      setError(`File validation failed: ${validationResult.errors.join('; ')}`);
      e.target.value = '';
      return;
    }

    setError('');
    setFiles(prevFiles => [...prevFiles, ...selectedFiles]);
  };

  const removeFile = (index) => {
    setFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
  };

  const getFileIconComponent = (fileName) => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    const codeExtensions = ['py', 'js', 'jsx', 'ts', 'tsx', 'java', 'cpp', 'c', 'cs', 'php', 'rb', 'go', 'sql', 'html', 'css', 'scss', 'less'];

    if (codeExtensions.includes(extension)) {
      return <FileCode className="h-4 w-4 text-green-600" />;
    } else {
      return <PaperclipIcon className="h-4 w-4 text-blue-600" />;
    }
  };

  const getFileCategory = (fileName) => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    const codeExtensions = ['py', 'js', 'jsx', 'ts', 'tsx', 'java', 'cpp', 'c', 'cs', 'php', 'rb', 'go', 'sql', 'html', 'css', 'scss', 'less'];

    if (codeExtensions.includes(extension)) {
      return 'Code';
    } else {
      return 'Document';
    }
  };





const handleSubmit = async (e) => {
  e.preventDefault();
  setIsSubmitting(true);
  setError('');


 // Check permissions first
  if (!permissions.canSubmitAnalysis) {
    setError('Insufficient permissions to submit legacy code analysis');
    setIsSubmitting(false);
    return;
  }


  // Validation (existing code...)
  if (!selectedLevelId) {
    setError('Please select a functional level before submitting');
    setIsSubmitting(false);
    return;
  }

  if (files.length === 0) {
    setError('Please upload at least one file for analysis');
    setIsSubmitting(false);
    return;
  }

  if (!description.trim()) {
    setError('Please provide a description for this analysis job');
    setIsSubmitting(false);
    return;
  }

  if (!defaultCriticality) {
    setError('Please select a default criticality level');
    setIsSubmitting(false);
    return;
  }

  if (!defaultPriority) {
    setError('Please select a default priority level');
    setIsSubmitting(false);
    return;
  }

  try {
    // Create FormData (existing code...)
    const formData = new FormData();
    formData.append('corporate_account', corporateAccount);
    formData.append('project_id', currentProject);
    formData.append('level_id', selectedLevelId);
    formData.append('analysis_type', analysisType);
    formData.append('description', description.trim());
    formData.append('user_id', userId);
    formData.append('user_email', userEmail);
    formData.append('default_status', defaultStatus);
    formData.append('default_criticality', defaultCriticality);
    formData.append('default_priority', defaultPriority);
    formData.append('functional_level_path', selectedFunctionalLevelPath);

    files.forEach((file) => {
      formData.append(`files[]`, file);
    });

    console.log('Submitting to enhanced legacy analysis endpoint...');

    const response = await fetch(appConfig.getUrl(API_ENDPOINTS.SUBMIT_ANALYSIS), {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${login_token}`
      },
      body: formData
    });

    const result = await response.json();

    console.log('Submit response:', result);

     if (response.ok && result.status === 'Success') {
      console.log('SUCCESS: Setting job ID:', result.job_id);

      setJobId(result.job_id);
      setError('');

      // Initialize states without expectedPhases
      setSubmitStatus('processing');
      setCurrentPhase('queued');
      setPhaseProgress([]);
      setQualityMetrics(null);

      // Start polling
      startPhasePolling(result.job_id);

      setTimeout(() => {
        setViewMode('success');
      }, 100);

    } else {
      throw new Error(result.status_description || 'Failed to submit analysis job');
    }
  } catch (error) {
    console.error('Error submitting enhanced legacy code analysis:', error);
    setError(`Failed to submit analysis job: ${error.message}`);
    setSubmitStatus('error');
  } finally {
    setIsSubmitting(false);
  }
};



  // Updated phase polling to use your existing endpoint
const startPhasePolling = (jobId) => {
  console.log('🔄 Starting phase polling for job:', jobId);

  const pollInterval = setInterval(async () => {
    try {
      const response = await fetch(appConfig.getUrl(`/get_enhanced_job_status/${jobId}`), {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${login_token}`
        }
      });

      const data = await response.json();

      // Enhanced logging
      console.log('=== ENHANCED POLLING RESPONSE ===');
      console.log('Job status:', data.job_status);
      console.log('Current phase:', data.current_phase);
      console.log('Phase progress count:', data.phase_progress?.length || 0);
      console.log('All phases:', data.phase_progress?.map(p => `${p.phase}:${p.status}`) || []);
      console.log('Expected phases:', data.expected_phases);
      console.log('=================================');

      if (data.status === 'Success') {
        // Update current phase
        if (data.current_phase) {
          setCurrentPhase(data.current_phase);
        }

        // Update phase progress - CRITICAL FIX
        // Update phase progress - CRITICAL FIX
        if (data.phase_progress && Array.isArray(data.phase_progress)) {
          console.log('✅ Setting phase progress with', data.phase_progress.length, 'phases');
          console.log('All phases:', data.phase_progress.map(p => `${p.phase}:${p.status}`));
          setPhaseProgress([...data.phase_progress]); // Force new array reference
        }


          // Update phase progress with error details
          if (data.phase_progress && Array.isArray(data.phase_progress)) {
            console.log('✅ Setting phase progress with', data.phase_progress.length, 'phases');
            console.log('All phases:', data.phase_progress.map(p => `${p.phase}:${p.status}`));

            // Enhance phase progress with error messages from the main job data
            const enhancedPhaseProgress = data.phase_progress.map(phase => ({
              ...phase,
              error_message: phase.status === 'failed' ? data.error_message || 'Phase processing failed' : undefined
            }));

            setPhaseProgress(enhancedPhaseProgress);
          }


        // Update quality metrics
        if (data.enhancement_results || data.quality_metrics) {
          const enhancementResults = data.enhancement_results || {};
          const qualityMetrics = data.quality_metrics || {};

          setQualityMetrics({
            average_confidence: enhancementResults.average_confidence || qualityMetrics.average_confidence || 0,
            high_confidence_count: enhancementResults.high_confidence_requirements || qualityMetrics.high_confidence_count || 0,
            analysis_phases_completed: enhancementResults.phases_completed || qualityMetrics.analysis_phases_completed || 0,
            total_analysis_phases: enhancementResults.total_phases || qualityMetrics.total_analysis_phases || data.phase_progress?.length || 7
          });
        }

        // Check completion
        if (data.job_status === 'completed') {
          console.log('🎉 Job completed! Stopping polling.');
          clearInterval(pollInterval);
          setSubmitStatus('success');
          if (viewMode !== 'requirements') {
            setViewMode('success');
          }
        } else if (data.job_status === 'failed') {
          console.log('❌ Job failed! Stopping polling.');
          clearInterval(pollInterval);
          setError(data.error_message || 'Analysis job failed');
          setSubmitStatus('error');
        }
      }
    } catch (error) {
      console.error('❌ Error polling job status:', error);
    }
  }, 3000);

  // Extended timeout
  setTimeout(() => {
    clearInterval(pollInterval);
    if (submitStatus === 'processing') {
      setError('Analysis is taking longer than expected. Check "View All Jobs" for status updates.');
    }
  }, 60 * 60 * 1000);
};

const isJobComplete = (data) => {
  // Job is complete if:
  // 1. Backend says it's completed, OR
  // 2. We're in finalization phase and it's marked as completed, OR
  // 3. We have final results with requirements created

  if (data.job_status === 'completed') {
    return true;
  }

  if (data.phase_progress && Array.isArray(data.phase_progress)) {
    const finalizationPhase = data.phase_progress.find(p => p.phase === 'finalization');
    if (finalizationPhase && finalizationPhase.status === 'completed') {
      return true;
    }
  }

  if (data.requirements_created && data.requirements_created > 0) {
    return true;
  }

  return false;
};


  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white p-4 flex items-center justify-center">
              <Sparkles className="mr-2" size={20} />

          <span className="text-lg font-bold">Code Analysis Using AI - Generate Requirements</span>

        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {viewMode === 'requirements' ? (
            // Requirements Management View
            <EnhancedRequirementsView
              jobId={jobId}
              onBack={() => setViewMode('success')}
              login_token={login_token}
              corporateAccount={corporateAccount}
              currentProject={currentProject}
              userId={userId}
            />
          ) : (submitStatus === 'success' || submitStatus === 'processing') && viewMode === 'success' ? (
            // Success View with NEW action buttons
            <div className="text-center py-8">


              <PhaseIndicator
                currentPhase={currentPhase}
                phaseProgress={phaseProgress}
                expectedPhases={7}
              />

              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-green-700 mb-2">
                  <strong>Job ID:</strong> {jobId}
                </p>
                <p className="text-sm text-green-700 mb-2">
                  <strong>Files Uploaded:</strong> {files.length} file{files.length !== 1 ? 's' : ''}
                </p>
                <p className="text-sm text-green-700 mb-2">
                  <strong>Target Functional Level:</strong> {selectedFunctionalLevelPath}
                </p>
                <p className="text-sm text-green-700">
                  <strong>Analysis Type:</strong> {analysisType.charAt(0).toUpperCase() + analysisType.slice(1)}
                </p>
              </div>

              {qualityMetrics && (
                <QualityMetrics
                  qualityMetrics={qualityMetrics}
                  analysisType="enhanced_iterative"
                />
              )}

              {/* NEW: Action Buttons for Requirements Management */}
              <div className="mt-6 space-y-3">


                  <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
                  <button
                    onClick={() => fetchJobRequirements(jobId)}
                    disabled={!isAnalysisComplete()}
                    className={`flex items-center px-6 py-3 rounded-lg shadow-lg justify-center ${
                      isAnalysisComplete()
                        ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    <FileText className="h-5 w-5 mr-2" />
                    <span className="mx-auto text-center w-full">
                      {isAnalysisComplete() ? 'View Requirements' : 'View Requirements (Analysis In Progress...)'}
                    </span>
                  </button>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 justify-center">





                </div>
              </div>

              {/* Processing Status for Optimization Round */}
              {optimizationInProgress && (
                <div className="mt-6 p-4 bg-purple-50 border border-purple-200 rounded-lg">
                  <div className="flex items-center justify-center">
                    <RefreshCw className="animate-spin mr-3 h-5 w-5 text-purple-600" />
                    <div className="text-purple-800">
                      <div className="font-medium">Starting Optimization Round...</div>
                      <div className="text-sm">This will enhance all requirements with additional AI analysis</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            // Form View (existing)
            <form onSubmit={handleSubmit} className="space-y-6">

              {/* Functional Level Selection */}
              <div className="p-3 bg-blue-50 border border-blue-200 rounded">
                <h4 className="font-semibold text-blue-800 mb-1">Target Functional Level:</h4>
                <select
                  ref={levelSelectRef}
                  value={selectedLevelId}
                  onChange={(e) => handleLevelSelect(e.target.value)}
                  className="w-full p-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  disabled={isLoadingLevels}
                  required
                >
                  <option value="">Select a functional level *</option>
                  {functionalLevels.map(level => (
                    <option key={level.level_id} value={level.level_id}>
                      {level.functional_level_hierarchy}
                    </option>
                  ))}
                </select>
                {selectedFunctionalLevelPath && (
                  <p className="text-sm text-blue-700 mt-1">
                    Selected: {selectedFunctionalLevelPath}
                  </p>
                )}
                {isLoadingLevels && (
                  <p className="text-sm text-blue-600 mt-1">Loading functional levels...</p>
                )}
              </div>

              {/* Analysis Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Analysis Description: <span className="text-red-500">*</span>
                </label>
                <textarea
                  ref={descriptionRef}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Describe the legacy system/code for enhanced analysis (e.g., 'Legacy customer management system built in Java with complex business rules and multiple integrations')"
                  className="w-full p-3 border border-gray-300 rounded-lg h-20 resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
                <p className="text-xs text-purple-600 mt-1">
                  💡 Tip: More detailed descriptions help the enhanced analysis build better context
                </p>
              </div>

              {/* Analysis Type */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Analysis Type:
                  </label>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setShowAnalysisTooltip(!showAnalysisTooltip)}
                      className="text-gray-400 hover:text-gray-600"
                      title="Click for comparison details"
                    >
                      <Info className="h-4 w-4" />
                    </button>
                    <AnalysisTypeTooltip
                      isVisible={showAnalysisTooltip}
                      onClose={() => setShowAnalysisTooltip(false)}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <label className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                    <input
                      type="radio"
                      name="analysisType"
                      value="comprehensive"
                      checked={analysisType === 'comprehensive'}
                      onChange={(e) => setAnalysisType(e.target.value)}
                      className="mr-3"
                    />
                    <div>
                      <div className="font-medium text-sm">Comprehensive</div>
                      <div className="text-xs text-gray-500">Full functional analysis</div>
                      <div className="text-xs text-blue-600 mt-1">30-60 requirements</div>
                    </div>
                  </label>
                  <label className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                    <input
                      type="radio"
                      name="analysisType"
                      value="security"
                      checked={analysisType === 'security'}
                      onChange={(e) => setAnalysisType(e.target.value)}
                      className="mr-3"
                    />
                    <div>
                      <div className="font-medium text-sm">Security Focused</div>
                      <div className="text-xs text-gray-500">Security requirements</div>
                      <div className="text-xs text-red-600 mt-1">25-50 requirements</div>
                    </div>
                  </label>
                  <label className="flex items-center p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                    <input
                      type="radio"
                      name="analysisType"
                      value="integration"
                      checked={analysisType === 'integration'}
                      onChange={(e) => setAnalysisType(e.target.value)}
                      className="mr-3"
                    />
                    <div>
                      <div className="font-medium text-sm">Integration</div>
                      <div className="text-xs text-gray-500">API & data integration</div>
                      <div className="text-xs text-green-600 mt-1">20-40 requirements</div>
                    </div>
                  </label>
                </div>
              </div>

              {/* Default Settings */}
              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium text-gray-700 mb-3">Default Settings for Generated Requirements:</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Status:</label>
                    <select
                      value={defaultStatus}
                      onChange={(e) => setDefaultStatus(e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded text-sm"
                    >
                      {availableStatuses.map(status => (
                        <option key={status} value={status}>{status}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                      Criticality: <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={defaultCriticality}
                      onChange={(e) => setDefaultCriticality(e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded text-sm"
                      required
                    >
                      <option value="">Select criticality</option>
                      {availableCriticalities.map(crit => (
                        <option key={crit} value={crit}>{crit}</option>
                      ))}
                    </select>
                  </div>


                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">
                        Priority: <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={defaultPriority}
                        onChange={(e) => setDefaultPriority(e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded text-sm"
                        required
                      >
                        <option value="">Select priority *</option>
                        {availablePriorities.map(priority => (
                          <option key={priority} value={priority}>{priority}</option>
                        ))}
                      </select>
                    </div>


                </div>
              </div>

              {/* File Upload Section */}
              <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                <h4 className="font-medium text-purple-800 mb-3">
                  Upload Legacy Code & Documents: <span className="text-red-500">*</span>
                </h4>

                <div className="mb-4">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="flex items-center px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Select Files
                  </button>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                    multiple
                    accept=".py,.js,.jsx,.ts,.tsx,.java,.cpp,.c,.cs,.php,.rb,.go,.sql,.html,.css,.scss,.less,.pdf,.doc,.docx,.xls,.xlsx,.txt,.md,.rtf,.png,.jpg,.jpeg,.gif"
                  />
                </div>

                {files.length > 0 && (
                  <div className="space-y-2">
                    <h5 className="text-sm font-medium text-purple-700">
                      Selected Files ({files.length}):
                    </h5>
                    <div className="max-h-40 overflow-y-auto space-y-2">
                      {files.map((file, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between p-2 bg-white border rounded"
                        >
                          <div className="flex items-center">
                            {getFileIconComponent(file.name)}
                            <span className="ml-2 text-sm truncate max-w-xs">{file.name}</span>
                            <span className="ml-2 text-xs text-gray-500">
                              ({formatFileSize(file.size)}) - {getFileCategory(file.name)}
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeFile(index)}
                            className="p-1 text-red-600 hover:text-red-800 rounded"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="mt-3 text-xs text-purple-600">
                  <p><strong>Supported file types:</strong></p>
                  <p><strong>Code:</strong> Python (.py), JavaScript (.js, .jsx), TypeScript (.ts, .tsx), Java (.java), C/C++ (.c, .cpp), C# (.cs), PHP (.php), Ruby (.rb), Go (.go), SQL (.sql), HTML/CSS (.html, .css, .scss, .less)</p>
                  <p><strong>Documents:</strong> PDF (.pdf), Word (.doc, .docx), Excel (.xls, .xlsx), Text (.txt, .md, .rtf), Images (.png, .jpg, .jpeg, .gif)</p>
                  <p><strong>Max file size:</strong> 10MB per file, 50MB total</p>
                </div>
              </div>



            </form>
          )}

          {/* Error Display */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded flex items-center">
              <AlertCircle className="text-red-500 mr-2" size={16} />
              <span className="text-red-700 text-sm">{error}</span>
            </div>
          )}
        </div>




        {/* Permission Warning */}
        {!permissions.canSubmitAnalysis && (
          <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded flex items-center">
            <AlertCircle className="text-yellow-500 mr-2" size={16} />
            <span className="text-yellow-700 text-sm">
              You do not have permission to submit legacy code analysis. Please contact your administrator.
            </span>
          </div>
        )}


        {/* Footer */}
        <div className="p-4 border-t border-gray-200 bg-gray-50 flex justify-between items-center">
          {viewMode === 'requirements' ? (
            <>
              <div className="text-sm text-gray-600">
                Requirements Management • Job ID: {jobId}
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setViewMode('success')}
                  className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
                >
                  Back to Summary
                </button>
                <button
                  onClick={onClose}
                  className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Close
                </button>
              </div>
            </>
          ) : submitStatus === 'success' ? (
            <>
              <div className="flex items-center gap-4">


                <div className="text-sm text-gray-600">
                  ✅ Analysis Complete • Enhanced Multi-Phase Processing
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleStartNewAnalysis}
                  className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                >
                  New Analysis
                </button>
                <button
                  onClick={onClose}
                  className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Close
                </button>
              </div>
            </>
          ) : (
            <>



 <div className="flex flex-row items-center justify-center gap-4 w-full">
  <button
    type="button"
    onClick={onClose}
    className="px-3 py-1 bg-yellow-200 text-gray-700 rounded hover:bg-yellow-400 text-sm font-medium w-40 h-10"
    disabled={isSubmitting}
  >
    Close
  </button>



<button
  onClick={handleSubmit}
  disabled={isSubmitting || isAnalysisInProgress() || !selectedLevelId || files.length === 0 || !description.trim() || !defaultCriticality || !defaultPriority || !permissions.canSubmitAnalysis}
  className={`px-6 py-2 rounded-lg font-medium flex items-center justify-center w-60 h-10 relative group ${
    isSubmitting || isAnalysisInProgress() || !selectedLevelId || files.length === 0 || !description.trim() || !defaultCriticality || !defaultPriority || !permissions.canSubmitAnalysis
      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
      : 'bg-gradient-to-r from-purple-600 to-blue-600 text-white hover:from-purple-700 hover:to-blue-700'
  }`}
>
  {isSubmitting || isAnalysisInProgress() ? (
    <>
      <RefreshCw className="animate-spin mr-2" size={18} />
      {isAnalysisInProgress() ? 'Analysis In Progress...' : 'Starting Analysis...'}
    </>
  ) : (
    <>
      <Sparkles className="mr-2 " size={20} /> Start Analysis
    </>
  )}

  {/* Tooltip for insufficient permissions */}
  {!permissions.canSubmitAnalysis && !isSubmitting && !isAnalysisInProgress() && (
    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded shadow-lg invisible group-hover:visible whitespace-nowrap z-20">
      Insufficient permission to submit analysis
      <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
    </div>
  )}
</button>





</div>




            </>
          )}
        </div>
      </div>

      {/* Enhanced Job History Modal */}
      {showJobHistory && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-5xl max-h-[80vh] flex flex-col overflow-hidden">
            <div className="bg-gradient-to-r from-purple-100 to-blue-100 p-4 flex items-center justify-between border-b">
              <div className="flex items-center">
                <History className="mr-3" size={20} />
                <span className="font-bold">My Enhanced Analysis Jobs</span>
                <span className="ml-2 text-sm text-gray-500">(Times shown in Eastern Time)</span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={fetchJobHistory}
                  className="flex items-center px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                >
                  <RefreshCw className="h-4 w-4 mr-1" />
                  Refresh
                </button>
                <button
                  onClick={() => setShowJobHistory(false)}
                  className="px-3 py-1 bg-yellow-200 text-gray-700 rounded hover:bg-yellow-400 text-sm font-medium"
                >
                  Close
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              {isLoadingJobs ? (
                <div className="text-center py-8">
                  <RefreshCw className="animate-spin mx-auto h-8 w-8 text-blue-500 mb-4" />
                  <p className="text-gray-600">Loading your enhanced analysis jobs...</p>
                </div>
              ) : jobHistory.length === 0 ? (
                <div className="text-center py-8">
                  <FileText className="mx-auto h-8 w-8 text-gray-400 mb-4" />
                  <p className="text-gray-600 mb-2">No enhanced analysis jobs found</p>
                  <p className="text-sm text-gray-500">Submit your first enhanced legacy code analysis to see it here.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {jobHistory.map((job) => (
                    <div
                      key={job.job_id}
                      className={`border rounded-lg p-4 ${
                        job.job_type === 'optimization'
                          ? 'border-purple-200 bg-gradient-to-r from-purple-50 to-pink-50'
                          : 'border-blue-200 bg-gradient-to-r from-blue-50 to-purple-50'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              job.status === 'completed' ? 'bg-green-50 text-green-600' :
                              job.status === 'processing' ? 'bg-blue-50 text-blue-600' :
                              job.status === 'failed' ? 'bg-red-50 text-red-600' :
                              'bg-gray-50 text-gray-600'
                            }`}>
                              {job.status === 'completed' && <CheckCircle className="h-3 w-3 mr-1" />}
                              {job.status === 'processing' && <RefreshCw className="h-3 w-3 mr-1 animate-spin" />}
                              {job.status === 'failed' && <X className="h-3 w-3 mr-1" />}
                              {job.status.charAt(0).toUpperCase() + job.status.slice(1)}
                            </span>

                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              job.job_type === 'optimization'
                                ? 'bg-purple-100 text-purple-700'
                                : 'bg-blue-100 text-blue-700'
                            }`}>
                              {job.job_type === 'optimization' ? (
                                <>
                                  <Sparkles className="h-3 w-3 mr-1" />
                                  {job.optimization_focus ? `${job.optimization_focus} Optimization` : 'Optimization'}
                                </>
                              ) : (
                                <>
                                <Sparkles className="mr-2" size={20} />
                                  Code & Document Analysis
                                </>
                              )}
                            </span>

                            <span className="text-xs text-gray-500 font-mono">{job.job_id}</span>
                          </div>

                          <h4 className="text-sm font-medium text-gray-900 mb-2">{job.description}</h4>

                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs text-gray-600 mb-3">
                            <div><span className="font-medium">Analysis Type:</span> {job.analysis_type}</div>
                            <div><span className="font-medium">Files:</span> {job.files_count}</div>
                            <div><span className="font-medium">Requirements:</span> {job.requirements_created || 0}</div>
                            <div><span className="font-medium">Processing Time:</span> {job.processing_time || 'N/A'}</div>
                          </div>

                          {/* Quality metrics from your backend */}
                          {job.quality_metrics && (
                            <div className="p-2 bg-white bg-opacity-50 border border-gray-200 rounded text-xs">
                              <div className="flex items-center justify-between">
                                <span className="font-medium text-gray-700">Quality Metrics:</span>
                                <div className="flex items-center space-x-3 text-gray-600">
                                  <span>Confidence: {(job.quality_metrics.average_confidence * 100).toFixed(1)}%</span>
                                  <span>High Quality: {job.quality_metrics.high_confidence_count}</span>
                                  {job.quality_metrics.optimization_improvement && (
                                    <span className="text-green-600">
                                      +{(job.quality_metrics.optimization_improvement * 100).toFixed(1)}% improved
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Parent Job Reference for Optimization Jobs */}
                          {job.parent_job_id && (
                            <div className="mt-2 text-xs text-purple-600">
                              <span className="font-medium">Optimization of:</span> {job.parent_job_id}
                            </div>
                          )}
                        </div>

                        {/* Action Buttons */}
                        <div className="flex flex-col gap-2 ml-4">
                          {job.status === 'completed' && (
                            <button
                              onClick={() => {
                                setJobId(job.job_id);
                                fetchJobRequirements(job.job_id);
                                setShowJobHistory(false);
                              }}
                              className="flex items-center px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                            >
                              <FileText className="h-3 w-3 mr-1" />
                              View Requirements
                            </button>
                          )}

                          {job.status === 'completed' && job.job_type !== 'optimization' && (
                            <button
                              onClick={async () => {
                                setJobId(job.job_id);
                                await handleStartOptimizationRound();
                                setShowJobHistory(false);
                              }}
                              className="flex items-center px-3 py-1 text-xs bg-purple-600 text-white rounded hover:bg-purple-700"
                            >
                              <Sparkles className="h-3 w-3 mr-1" />
                              Optimize
                            </button>
                          )}

                          {job.status === 'failed' && (
                            <button
                              onClick={() => handleRetryJob(job.job_id)}
                              className="flex items-center px-3 py-1 text-xs bg-orange-600 text-white rounded hover:bg-orange-700"
                            >
                              <RotateCcw className="h-3 w-3 mr-1" />
                              Retry
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Enhanced Summary Footer */}
            <div className="border-t border-gray-200 p-4 bg-gradient-to-r from-gray-50 to-blue-50">
              <h4 className="text-sm font-semibold text-gray-700 mb-2">Enhanced Analysis Summary</h4>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-xs">
                <div className="text-center">
                  <div className="font-semibold text-gray-900">{jobHistory.length}</div>
                  <div className="text-gray-600">Total Jobs</div>
                </div>
                <div className="text-center">
                  <div className="font-semibold text-green-900">
                    {jobHistory.reduce((sum, job) => sum + (job.requirements_created || 0), 0)}
                  </div>
                  <div className="text-green-600">Total Requirements</div>
                </div>
                <div className="text-center">
                  <div className="font-semibold text-purple-900">
                    {jobHistory.filter(job => job.job_type === 'optimization').length}
                  </div>
                  <div className="text-purple-600">Optimization Rounds</div>
                </div>
                <div className="text-center">
                  <div className="font-semibold text-blue-900">
                    {jobHistory.length > 0 && jobHistory[0].quality_metrics
                      ? (jobHistory[0].quality_metrics.average_confidence * 100).toFixed(1) + '%'
                      : '0%'}
                  </div>
                  <div className="text-blue-600">Latest Confidence</div>
                </div>
                <div className="text-center">
                  <div className="font-semibold text-gray-900">100%</div>
                  <div className="text-gray-600">Enhanced</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnhancedLegacyCodeAnalysisDialog;